import isApplicationUneditable from '@bybeam/platform-lib/application/isApplicationUneditable';
import { ApplicationWithCase } from '@bybeam/platform-lib/application/reapplication';
import sort from '@bybeam/platform-lib/utilities/sort';
import {
  ApplicantTypeRole,
  Application,
  CaseStatus,
  PaymentStatus,
  Program,
  User,
} from '@bybeam/platform-types';
import Icon from '@platform-ui-common/components/Icon/index.js';
import Link from '@platform-ui-common/components/Link/index.js';
import { Column } from '@platform-ui-common/components/Table/types.js';
import Tooltip from '@platform-ui-common/components/Tooltip/index.js';
import { getUniqueApplicantTypes } from '@platform-ui-common/utilities/applicantType.js';
import { DateFormat, formatCurrency, formatDate } from '@platform-ui-common/utilities/format.js';
import { formatSimpleCaseStatus } from '@platform-ui-common/utilities/statuses.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import resumeApplication from '@platform-ui/application/utils/resumeApplication.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { getApplicationConfiguration } from '@platform-ui/utilities/application/configs/index.js';
import { hasApplicantType, programHasPayments } from '@platform-ui/utilities/programs.js';
import { ReactNode } from 'react';

export interface TableRow {
  id?: string;
  caseId: {
    displayId: string;
    link?: string;
  };
  firstPartyName: string;
  status: {
    status: string;
    link?: string;
  };
  submissionDate: string;
  programName: {
    name: string;
    hasReferral: boolean;
  };
  viewActivity: {
    link: string;
  };
}

const LinkWrapper = ({ to, children }: { to?: string; children: ReactNode }) =>
  to ? <Link to={to}>{children}</Link> : children;

export const getColumns = (allPrograms: Program[], user: User): Column<TableRow>[] => {
  const programs = allPrograms.filter((program) => hasApplicantType(program, user));
  return [
    {
      Header: 'Case ID',
      accessor: 'caseId',
      Cell: function CaseIdCell({ value: { displayId, link } }): JSX.Element {
        return (
          <LinkWrapper to={link}>
            <strong>{displayId}</strong>
          </LinkWrapper>
        );
      },
    },
    ...(user.applicantProfile.applicantType.role === ApplicantTypeRole.ThirdParty
      ? [
          {
            Header: `${
              getUniqueApplicantTypes(programs).find(
                ({ role }) => role === ApplicantTypeRole.FirstParty,
              ).name
            } Name`,
            accessor: 'firstPartyName' as const,
          },
        ]
      : []),
    {
      Header: 'Program Name',
      accessor: 'programName',
      Cell: function ProgramNameCell({ value: { name, hasReferral } }): JSX.Element {
        return (
          <span className="flex gap-2">
            {name}
            {hasReferral && (
              <Tooltip title="Referred">
                <Icon size="MD" type="referral" />
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      Header: 'Submission Date',
      accessor: 'submissionDate',
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: function StatusCell({ value: { status, link } }): JSX.Element {
        return (
          <LinkWrapper to={link}>
            <strong>{status}</strong>
          </LinkWrapper>
        );
      },
    },
    {
      Header: '',
      accessor: 'viewActivity',
      Cell: function ActivityCell({ value: { link } }): JSX.Element {
        return (
          <LinkWrapper to={link}>
            <strong>View Activity</strong>
          </LinkWrapper>
        );
      },
    },
  ];
};

export const prepData = (programs: Program[], data: Application[], user: User): TableRow[] => {
  if (!data) return [];
  const noop = '-';
  return sort(data, { accessor: (app) => app.submittedAt, ascending: false }).map((application) => {
    const { case: case_, submittedAt } = application;
    const { displayId, name, status, decisionReachedAt } = case_ ?? {};
    const program = programs.find((program) => program.id === case_?.program?.id);
    const fulfillments = case_?.fulfillments ?? [];
    const approvedAmount = fulfillments?.length
      ? formatCurrency(
          fulfillments
            .filter((fulfillment) =>
              fulfillment.payments.every((each) => each.status !== PaymentStatus.Pending),
            )
            .reduce((acc, cur) => acc + cur.approvedAmount, 0),
          true,
        )
      : 'N/A';
    const programConfig = getApplicationConfiguration(program, user);
    const isEditable = !isApplicationUneditable(application as ApplicationWithCase);

    const params = {
      id: application.id,
      caseId: {
        displayId,
        link: makeRoute(ApplicantRoutes.CASE_DETAILS, {
          caseId: application.case.id,
        }),
      },
      firstPartyName:
        user.applicantProfile.applicantType.role === ApplicantTypeRole.ThirdParty &&
        name === user.name
          ? '-'
          : name,
      submissionDate: submittedAt ? formatDate(submittedAt, DateFormat.Slashes) : noop,
      status: {
        status:
          status === CaseStatus.Approved && programHasPayments(program)
            ? `Approved - ${approvedAmount}`
            : formatSimpleCaseStatus(application?.case?.status),
        ...(isEditable && {
          link: makeRoute(resumeApplication(programConfig, application), {
            applicationId: application.id,
          }),
        }),
      },
      programName: {
        name: program?.name,
        hasReferral: !!application.referral,
      },
      viewActivity: {
        link: `${makeRoute(ApplicantRoutes.CASE_DETAILS, {
          caseId: application.case.id,
        })}/#activity-section`,
      },
    };

    return params;
  });
};
